import { Auth } from 'aws-amplify';
import axios from 'axios';
import { API_LOCATION } from '@/api-config';

/**
 * Decode a stored waiver signature into a usable <img> src.
 *
 * Signatures are stored as a `data:image/png;base64,...` STRING inside a BLOB
 * column. The MySQL driver serializes that BLOB to JSON as
 * `{ type: 'Buffer', data: [byte, byte, ...] }`, so the bytes we receive are the
 * UTF-8/ASCII bytes of the data-URL string. Decoding them back to a string yields
 * the data URL directly.
 *
 * The legacy implementation used `String.fromCharCode.apply(null, byteArray)`,
 * which spreads the whole array as function arguments and STACK-OVERFLOWS (taking
 * the page down) on large signatures. We decode in fixed-size chunks instead.
 *
 * @param {string|{data:number[]}|number[]|Uint8Array|null|undefined} raw
 * @returns {string} a data-URL usable as an <img> src, or '' when absent
 */
export function renderSignature(raw) {
  if (raw == null) return '';
  // Already a data URL (some callers pass the pre-decoded string).
  if (typeof raw === 'string') return raw;

  // Accept { type:'Buffer', data:[...] }, a bare array, or a typed array.
  const source = Array.isArray(raw) || raw instanceof Uint8Array ? raw : raw.data;
  if (!source || source.length === 0) return '';

  const bytes = source instanceof Uint8Array ? source : new Uint8Array(source);
  const CHUNK = 8192;
  let out = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    out += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return out;
}

async function getAuthToken() {
  const session = await Auth.currentSession();
  return session.getIdToken().getJwtToken();
}

/**
 * Fetch a single waiver's full detail from the modern single-GET endpoint
 * (`waivers-api-get-waiver`). Pass `{ includeSignature: true }` to also pull the
 * heavy signature bytes — omit it on surfaces that only need metadata so the
 * payload stays small.
 *
 * Returns `{ waiver, custom_field_responses, consent_fields }`. When the
 * signature is requested and present, `waiver.signature` is the decoded data-URL
 * string, ready to drop straight into an <img> src.
 *
 * `companyId` / `groupId` default to the active company + group in localStorage,
 * matching every other waiver fetch in the app.
 *
 * @param {number|string} waiverId
 * @param {{ includeSignature?: boolean, companyId?: number|string, groupId?: number|string }} [opts]
 * @returns {Promise<{ waiver: object, custom_field_responses: array, consent_fields: array }>}
 */
export async function fetchWaiverDetail(waiverId, opts = {}) {
  const { includeSignature = false, companyId, groupId } = opts;
  const cid = companyId != null ? companyId : localStorage.getItem('companyId');
  const gid = groupId != null ? groupId : JSON.parse(localStorage.getItem('group')).id;

  const token = await getAuthToken();
  const config = { headers: { Authorization: token } };
  let url = `${API_LOCATION}companies/${cid}/groups/${gid}/waivers/${waiverId}`;
  if (includeSignature) url += '?include=signature';

  const res = await axios.get(url, config);
  const data = res.data || {};
  const waiver = data.waiver || {};
  if (includeSignature && waiver.signature != null) {
    waiver.signature = renderSignature(waiver.signature);
  }
  return {
    waiver,
    custom_field_responses: data.custom_field_responses || [],
    consent_fields: data.consent_fields || [],
  };
}
